<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { computed, reactive, ref } from 'vue';
import { string, object } from 'yup';

const {
    tenant,
    googleAdsAudience,
    googleAdsAudienceTypes,
    googleAdsAudienceCustomerTypeCategories,
} = defineProps({
    tenant: {
        required: true,
        type: Object,
    },
    googleAdsAudience: {
        required: false,
        type: Object,
    },
    googleAdsAudienceTypes: {
        required: true,
        type: Array,
    },
    googleAdsAudienceCustomerTypeCategories: {
        required: true,
        type: Array,
    },
});

const { route } = useRoute();

const form = reactive({
    type: googleAdsAudience
        ? googleAdsAudienceTypes.find(
              (type) => type.value === googleAdsAudience.type.value,
          )
        : googleAdsAudienceTypes[0],
    data: googleAdsAudience ? googleAdsAudience.data : {},
    name: googleAdsAudience ? googleAdsAudience.name : '',
    customerTypeCategory: googleAdsAudience
        ? googleAdsAudienceCustomerTypeCategories.find(
              (category) =>
                  category.value ===
                  googleAdsAudience.customerTypeCategory.value,
          )
        : googleAdsAudienceCustomerTypeCategories[0],
});

const isSubmitting = ref(false);

const validationSchema = computed(() => {
    let schema = {
        type: object().required('Ce champ est obligatoire.'),
        name: string().required('Ce champ est obligatoire.'),
        customerTypeCategory: object().required('Ce champ est obligatoire.'),
    };

    if (
        form.type.value === 'customers_who_have_bought_n_time' ||
        form.type.value === 'customers_who_have_never_bought_last_n_days' ||
        form.type.value === 'customers_who_have_bought_more_than_n_time' ||
        form.type.value === 'customers_who_have_unprocessed_cart_last_n_days' ||
        form.type.value === 'customers_who_have_bought_last_n_days' ||
        form.type.value ===
            'customers_who_have_not_bought_last_n_days_but_bought_before' ||
        form.type.value ===
            'customers_who_have_not_bought_last_n_days_but_bought_last_t_days'
    ) {
        schema = {
            ...schema,
            data: object().shape({
                n: string().required('Ce champ est obligatoire.'),
            }),
        };
    }

    if (
        form.type.value ===
            'customers_who_have_bought_more_than_n_time_last_t_days' ||
        form.type.value ===
            'customers_who_have_not_bought_last_n_days_but_bought_last_t_days'
    ) {
        schema = {
            ...schema,
            data: object().shape({
                n: string().required('Ce champ est obligatoire.'),
                t: string().required('Ce champ est obligatoire.'),
            }),
        };
    }

    return object(schema);
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.sea.postAudience', {
                tenant: tenant.id,
                ...(googleAdsAudience
                    ? { googleAdsAudienceId: googleAdsAudience.id }
                    : {}),
            }),
            {
                type: form.type.value,
                data: form.data,
                name: form.name,
                customerTypeCategory: form.customerTypeCategory.value,
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route(
                    'front.projectManagement.sea.audiences',
                    tenant ? { tenant: tenant.id } : {},
                ),
            );
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <FormRoot
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <FormColumn>
            <FormContent>
                <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2">
                    <InputField
                        v-model="form.name"
                        label="Nom"
                        field-name="name"
                        field-id="name"
                        type="text"
                        required
                    />

                    <ListboxField
                        v-model="form.customerTypeCategory"
                        field-id="customerTypeCategory"
                        field-name="customerTypeCategory"
                        label="Catégorie type client"
                        :options="googleAdsAudienceCustomerTypeCategories"
                        required
                    >
                        <template #buttonContent>
                            {{ form.customerTypeCategory.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>
                </div>

                <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2">
                    <ListboxField
                        v-model="form.type"
                        field-id="type"
                        field-name="type"
                        label="Type"
                        :options="googleAdsAudienceTypes"
                        required
                    >
                        <template #buttonContent>
                            {{ form.type.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>
                </div>

                <div
                    class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4"
                >
                    <InputField
                        v-if="
                            form.type.value ===
                                'customers_who_have_bought_n_time' ||
                            form.type.value ===
                                'customers_who_have_unprocessed_cart_last_n_days' ||
                            form.type.value ===
                                'customers_who_have_never_bought_last_n_days' ||
                            form.type.value ===
                                'customers_who_have_bought_more_than_n_time' ||
                            form.type.value ===
                                'customers_who_have_bought_more_than_n_time_last_t_days' ||
                            form.type.value ===
                                'customers_who_have_bought_last_n_days' ||
                            form.type.value ===
                                'customers_who_have_not_bought_last_n_days_but_bought_before' ||
                            form.type.value ===
                                'customers_who_have_not_bought_last_n_days_but_bought_last_t_days'
                        "
                        v-model="form.data.n"
                        label="N"
                        field-name="data.n"
                        field-id="data.n"
                        type="number"
                        step="1"
                        min="1"
                        required
                    />

                    <InputField
                        v-if="
                            form.type.value ===
                                'customers_who_have_bought_more_than_n_time_last_t_days' ||
                            form.type.value ===
                                'customers_who_have_not_bought_last_n_days_but_bought_last_t_days'
                        "
                        v-model="form.data.t"
                        label="T"
                        field-name="data.t"
                        field-id="data.t"
                        type="number"
                        step="1"
                        min="1"
                        required
                    />
                </div>
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="googleAdsAudience"
                    :url="
                        route('front.projectManagement.sea.deleteAudience', {
                            tenant: tenant.id,
                            googleAdsAudienceId: googleAdsAudience.id,
                        })
                    "
                    :callback="
                        () => {
                            route.visit(
                                'front.projectManagement.sea.audiences',
                                {
                                    tenant: tenant.id,
                                },
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
